/**
 * The manufacturability report.
 *
 * Grouped by rule, and that is not a presentation detail. A board of any age
 * breaks the same rule in the same way a few hundred times — every hole on a
 * connector has the same annular ring, so one footprint produces forty
 * findings — and a flat list of those buries the one thing that will actually
 * stop the board being made. Grouped, forty rows become a heading that says
 * "annular ring, 40" and a handful of examples under it.
 *
 * Worst first inside each group, so the first row you read is the one nearest
 * to being a reject.
 *
 * The rules live in a dialog of their own reached from the top of this one,
 * because the first question a report raises is "against what?" and the answer
 * has to be one click away.
 */

import type { DfmFinding, DfmReport } from '@sircad/core/dfm/check.ts';
import {
  RULE_IDS, RULE_LABELS, allProfiles, isCustom,
  type DfmProfile, type DfmRules, type RuleId,
} from '@sircad/core/dfm/rules.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface DfmReportOptions {
  report: DfmReport;
  /** The rules it was measured against, and what they are called. */
  rules: DfmRules;
  profileName: string;
  /** Sets the user has saved, offered beside the ones that ship. */
  saved?: DfmProfile[];
  /** Render a length in the user's units. */
  formatLength(inches: number): string;
  /** Jump the view to a point and select what is wrong there. */
  onLocate(x: number, y: number, entityIds?: number[]): void;
  /** Run it again against a different set. */
  onProfile(id: string): void;
  /** Open the rule editor. */
  onEditRules(): void;
}

/** Examples shown per rule before the rest are summarised. */
const PER_RULE = 12;

export function showDfmReport(opts: DfmReportOptions): void {
  const { report } = opts;

  openDialog({
    title: 'Manufacturability',
    wide: true,
    build(body, handle) {
      // --- What was checked, and against what -------------------------------
      const head = document.createElement('div');
      head.className = 'dfm-head';

      const verdict = document.createElement('p');
      verdict.className = report.errors > 0 ? 'dfm-verdict bad'
        : report.warnings > 0 ? 'dfm-verdict warn' : 'dfm-verdict good';
      verdict.textContent = report.errors > 0
        ? `${report.errors} problem${report.errors === 1 ? '' : 's'} that would stop this being made`
          + (report.warnings ? `, and ${report.warnings} worth looking at` : '')
        : report.warnings > 0
          ? `Nothing that would stop this being made — ${report.warnings} worth looking at`
          : 'Clean against every rule checked.';
      head.append(verdict);

      const against = document.createElement('div');
      against.className = 'dfm-against';
      const label = document.createElement('span');
      label.textContent = 'Checked against';
      const pick = document.createElement('select');
      const offered = allProfiles(opts.saved ?? []);
      for (const p of offered) {
        const o = document.createElement('option');
        o.value = p.id;
        o.textContent = isCustom(p.id) ? `${p.name} (saved)` : p.name;
        pick.append(o);
      }
      pick.value = offered.find((p) => p.name === opts.profileName)?.id ?? '';
      pick.addEventListener('change', () => {
        handle.close();
        opts.onProfile(pick.value);
      });
      against.append(label, pick);
      const edit = document.createElement('button');
      edit.type = 'button';
      edit.className = 'btn';
      edit.textContent = 'Rules…';
      edit.addEventListener('click', () => { handle.close(); opts.onEditRules(); });
      against.append(edit);
      head.append(against);
      body.append(head);

      // --- The findings, by rule --------------------------------------------
      const byRule = new Map<RuleId, DfmFinding[]>();
      for (const f of report.findings) {
        const list = byRule.get(f.rule);
        if (list) list.push(f); else byRule.set(f.rule, [f]);
      }

      for (const rule of RULE_IDS) {
        const found = byRule.get(rule);
        if (!found || found.length === 0) continue;

        // Worst first: the smallest gap, or the largest overshoot.
        found.sort((a, b) => (a.actual ?? 0) - (b.actual ?? 0));

        const errs = found.filter((f) => f.severity === 'error').length;
        body.append(ruleHeading(rule, found.length, errs));

        const why = document.createElement('p');
        why.className = 'dfm-why';
        why.textContent = RULE_LABELS[rule].why;
        body.append(why);

        for (const f of found.slice(0, PER_RULE)) body.append(row(f, opts));
        if (found.length > PER_RULE) {
          const more = document.createElement('p');
          more.className = 'modal-hint';
          more.textContent = `and ${found.length - PER_RULE} more like it — `
            + 'the worst are shown first.';
          body.append(more);
        }
      }

      // --- What passed, and what could not be asked -------------------------
      //
      // A report that lists only failures leaves you guessing whether the rest
      // was checked or merely not implemented. Saying what passed is what makes
      // a clean report mean something.
      if (report.clean.length > 0) {
        const ok = document.createElement('p');
        ok.className = 'dfm-passed';
        ok.textContent = `Passed: ${report.clean.map((r) => RULE_LABELS[r].title).join(', ')}.`;
        body.append(ok);
      }
      for (const s of report.skipped) {
        const p = document.createElement('p');
        p.className = 'dfm-skipped';
        p.textContent = `${RULE_LABELS[s.rule].title} was not checked — ${s.why}.`;
        body.append(p);
      }

      const bar = buttons(body);
      button(bar, 'Close', () => handle.close(), true);
    },
  });
}

function ruleHeading(rule: RuleId, n: number, errors: number): HTMLElement {
  const h = document.createElement('div');
  h.className = 'dfm-rule';
  const name = document.createElement('span');
  name.textContent = RULE_LABELS[rule].title;
  const count = document.createElement('span');
  count.className = errors > 0 ? 'dfm-count bad' : 'dfm-count warn';
  // Both numbers where they differ: "40, 12 serious" says more than either.
  count.textContent = errors > 0 && errors < n ? `${n}, ${errors} serious`
    : errors > 0 ? `${n}` : `${n}`;
  h.append(name, count);
  return h;
}

/** One finding: what is wrong, by how much, and a way to go and look at it. */
function row(f: DfmFinding, opts: DfmReportOptions): HTMLElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = `net-report-row dfm-row ${f.severity}`;

  const what = document.createElement('span');
  what.className = 'nr-net';
  what.textContent = f.detail;

  const numbers = document.createElement('span');
  numbers.className = 'nr-pins';
  // A negative gap is copper that overlaps, and printing it as "-0.0031" reads
  // as a fault in the tool rather than in the board. The detail already says
  // "touch" or "overlap" in that case, so only the rule is worth repeating.
  numbers.textContent = f.actual === undefined ? ''
    : f.actual <= 0 ? `rule ${opts.formatLength(f.required)}`
      : `${opts.formatLength(f.actual)} — rule ${opts.formatLength(f.required)}`;

  el.append(what, numbers);

  if (f.at) {
    el.title = 'Go to it';
    const at = f.at;
    el.addEventListener('click', () => opts.onLocate(at.x, at.y, f.entityIds));
  } else {
    el.disabled = true;
    el.title = 'This is about the board as a whole, so there is nowhere to go';
  }
  return el;
}
